import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatShortDate } from '../utils/dateUtils';
import './RateForm.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Persistent per-browser id so the admin view can group submissions from
// the same device. We also write a per-playlist marker on submit to power
// the "already rated" state below.
function getOrCreateClientToken() {
  const KEY = 'mr_rate_client_token';
  let t = localStorage.getItem(KEY);
  if (!t) {
    t =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(KEY, t);
  }
  return t;
}
function alreadyRatedKey(playlistId) {
  return `mr_rated_${playlistId}`;
}

function StarRow({ value, onChange, size = 'm', readOnly = false }) {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div
      className={`rf-stars rf-stars-${size}`}
      role="radiogroup"
      aria-label="Rating"
    >
      {stars.map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={n === value}
          className={`rf-star ${n <= (value || 0) ? 'on' : ''}`}
          onClick={() => !readOnly && onChange(value === n ? 0 : n)}
          aria-label={`${n} star${n === 1 ? '' : 's'}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function RateForm() {
  const { id } = useParams();
  const [search] = useSearchParams();
  // Dev-only: ?_now=<ISO> lets us simulate "what would 10am on Jun 28
  // look like?" without faking the system clock. Backend ignores it in
  // production. The form forwards it on both the /window pre-flight and
  // the POST submission so the rest of the UI behaves naturally.
  const fakeNow = search.get('_now') || null;
  const qsForNow = fakeNow ? `?_now=${encodeURIComponent(fakeNow)}` : '';
  const clientToken = useMemo(() => getOrCreateClientToken(), []);

  const [loading, setLoading] = useState(true);
  const [windowStatus, setWindowStatus] = useState(null);
  const [error, setError] = useState(null);
  const [alreadyRated, setAlreadyRated] = useState(
    () => !!localStorage.getItem(alreadyRatedKey(id))
  );

  // form
  const [overall, setOverall] = useState(0);
  const [music, setMusic] = useState(0);
  const [instructing, setInstructing] = useState(0);
  const [other, setOther] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/ratings/playlist/${id}/window${qsForNow}`)
      .then((r) => { if (!cancelled) setWindowStatus(r.data); })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) setError('Class not found.');
        else setError('Could not load this class.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, qsForNow]);

  async function submit() {
    if (submitting) return;
    if (overall < 1) {
      setSubmitErr('Please tap an overall rating first.');
      return;
    }
    setSubmitErr(null);
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/ratings/playlist/${id}${qsForNow}`, {
        overall,
        music: music || null,
        instructing: instructing || null,
        other: other || null,
        comment: comment.trim() || null,
        client_token: clientToken
      });
      localStorage.setItem(alreadyRatedKey(id), String(Date.now()));
      setSubmitted({
        overall, music, instructing, other, comment, ...r.data
      });
    } catch (err) {
      const msg = err.response?.data?.error || 'Submit failed';
      // If the server says the window just closed, flip the UI.
      if (err.response?.status === 403 && err.response?.data?.reason) {
        setWindowStatus({
          ...windowStatus,
          open: false,
          reason: err.response.data.reason
        });
        return;
      }
      setSubmitErr(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // ---------- render states ----------
  if (loading) {
    return (
      <div className="rf-shell">
        <div className="rf-phone">
          <div className="rf-screen rf-center">
            <div className="rf-spinner" />
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="rf-shell">
        <div className="rf-phone">
          <div className="rf-screen rf-center rf-pad">
            <div className="rf-icon">😕</div>
            <h1>{error}</h1>
          </div>
        </div>
      </div>
    );
  }

  // post-submit thanks
  if (submitted) {
    return (
      <div className="rf-shell">
        <div className="rf-phone">
          <div className="rf-screen rf-center rf-pad">
            <div className="rf-check">✓</div>
            <h1>Thanks for riding</h1>
            <p className="rf-sub">Your rating's locked in. Matt sees it the moment you submit.</p>
            <div className="rf-receipt">
              <h2>What you submitted</h2>
              <ReceiptRow label="Overall" stars={submitted.overall} />
              {!!submitted.music && <ReceiptRow label="Music" stars={submitted.music} />}
              {!!submitted.instructing && <ReceiptRow label="Instructing" stars={submitted.instructing} />}
              {!!submitted.other && <ReceiptRow label="Other" stars={submitted.other} />}
              {!!submitted.comment && (
                <div className="rf-receipt-row">
                  <span className="lbl">Comment</span>
                  <span className="val">"{submitted.comment}"</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // already-rated (localStorage marker present and no new submit pending)
  if (alreadyRated) {
    return (
      <div className="rf-shell">
        <div className="rf-phone">
          <div className="rf-screen rf-center rf-pad">
            <div className="rf-check">✓</div>
            <h1>You already rated this class</h1>
            <p className="rf-sub">Looks like you submitted from this device earlier. Matt got it — thank you.</p>
            <button
              className="rf-ghost"
              type="button"
              onClick={() => {
                localStorage.removeItem(alreadyRatedKey(id));
                setAlreadyRated(false);
              }}
            >
              Rate again from this device
            </button>
          </div>
        </div>
      </div>
    );
  }

  // closed states
  if (!windowStatus?.open) {
    const reason = windowStatus?.reason;
    let icon = '🌙';
    let title = 'Rating closed';
    let body = 'This class can\'t be rated right now.';
    if (reason === 'before-open') {
      icon = '⏳';
      title = 'Rating opens at 9:30am';
      body = 'You scanned a little early. Come back after class — the form unlocks at 9:30 and closes at noon MT.';
    } else if (reason === 'after-close') {
      title = 'Rating closed';
      body = 'Today\'s class can\'t be rated anymore — the window was 9:30am to noon MT.';
    } else if (reason === 'after-class-date' || reason === 'before-class-date') {
      title = 'Rating window not active';
      body = `Ratings open from 9:30am – noon MT only on the class date${windowStatus?.playlist?.date ? ` (${formatShortDate(windowStatus.playlist.date)})` : ''}.`;
    }
    return (
      <div className="rf-shell">
        <div className="rf-phone">
          <div className="rf-screen rf-center rf-pad">
            <div className="rf-icon">{icon}</div>
            <h1>{title}</h1>
            <p className="rf-sub">{body}</p>
          </div>
        </div>
      </div>
    );
  }

  // ---------- open: the form ----------
  return (
    <div className="rf-shell">
      <div className="rf-phone">
        <div className="rf-screen">
          <div className="rf-form">
            <div className="rf-class-line">
              <b>{windowStatus.playlist?.date
                  ? formatShortDate(windowStatus.playlist.date)
                  : 'Today'}</b>
            </div>

            <div className="rf-hero">
              <div className="rf-prompt">How was today's ride?</div>
              <StarRow value={overall} onChange={setOverall} size="l" />
            </div>

            <div className="rf-opt-stack" role="group" aria-label="Optional ratings">
              <div className="rf-opt-row">
                <span className="rf-opt-label"><span className="rf-opt-icon">🎵</span>Music</span>
                <StarRow value={music} onChange={setMusic} size="s" />
              </div>
              <div className="rf-opt-row">
                <span className="rf-opt-label"><span className="rf-opt-icon">🎤</span>Instructing</span>
                <StarRow value={instructing} onChange={setInstructing} size="s" />
              </div>
              <div className="rf-opt-row">
                <span className="rf-opt-label"><span className="rf-opt-icon">✨</span>Anything else</span>
                <StarRow value={other} onChange={setOther} size="s" />
              </div>
            </div>
            <div className="rf-opt-hint">optional</div>

            <textarea
              rows="4"
              placeholder="Tell Matt something (optional, private)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={2000}
            />

            {submitErr && <div className="rf-err">{submitErr}</div>}
          </div>

          <div className="rf-sticky">
            <button
              className="rf-submit"
              type="button"
              onClick={submit}
              disabled={submitting || overall < 1}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReceiptRow({ label, stars }) {
  const full = '★'.repeat(stars) + '☆'.repeat(5 - stars);
  return (
    <div className="rf-receipt-row">
      <span className="lbl">{label}</span>
      <span className="val">{full} · {stars}</span>
    </div>
  );
}

export default RateForm;
